import type { SVGProps } from 'react';
import type { VehicleType } from '@/state/types';

/**
 * Stylised generic silhouettes — one per category.
 * Type-specific silhouettes (HLF vs LF vs DLK …) come in Phase 6 (Polish).
 *
 * The SVGs use fill="currentColor" so the parent controls colour via
 * Tailwind text-* classes; that keeps them theme-friendly.
 */

import { silhouetteCategoryFor } from './category';
import {
  ElwSilhouette,
  MtwSilhouette,
  DlkSilhouette,
  PkwSilhouette,
  TlfSilhouette,
  HlfSilhouette,
  LfSilhouette,
  GwSilhouette,
  RwSilhouette,
  SwSilhouette,
  TsfSilhouette,
  FwkSilhouette,
  KtwSilhouette,
  RtwSilhouette,
  NefSilhouette,
  FuStWSilhouette,
  GruKWSilhouette,
  KradPolSilhouette,
  WaWeSilhouette,
  SEKWgSilhouette,
  MEKWgSilhouette,
  PolHeloSilhouette,
} from './types';

interface VehicleSilhouetteProps extends SVGProps<SVGSVGElement> {
  type: VehicleType;
}

const TYPE_MAP: Record<VehicleType, (p: SVGProps<SVGSVGElement>) => JSX.Element> = {
  ELW: ElwSilhouette,
  MTW: MtwSilhouette,
  DLK: DlkSilhouette,
  PKW: PkwSilhouette,
  TLF: TlfSilhouette,
  HLF: HlfSilhouette,
  LF: LfSilhouette,
  GW: GwSilhouette,
  RW: RwSilhouette,
  SW: SwSilhouette,
  TSF: TsfSilhouette,
  FWK: FwkSilhouette,
  // Phase 13.5 — GW-Varianten teilen sich die GW-Silhouette.
  GWL: GwSilhouette,
  GWG: GwSilhouette,
  KTW: KtwSilhouette,
  RTW: RtwSilhouette,
  NEF: NefSilhouette,
  FuStW: FuStWSilhouette,
  GruKW: GruKWSilhouette,
  KradPol: KradPolSilhouette,
  // Phase 13.3 — PolPKW als ziviler Streifenwagen → FuStW-Look.
  PolPKW: FuStWSilhouette,
  // Phase 13.3 — GefKW als gepanzerter Transporter → GruKW-Look.
  GefKW: GruKWSilhouette,
  WaWe: WaWeSilhouette,
  SEKWg: SEKWgSilhouette,
  MEKWg: MEKWgSilhouette,
  PolHelo: PolHeloSilhouette,
};

export function VehicleSilhouette({ type, ...rest }: VehicleSilhouetteProps) {
  const Specific = TYPE_MAP[type];
  if (Specific) return <Specific {...rest} />;
  // Should never happen for known types, but keep generics as a safety net.
  const cat = silhouetteCategoryFor(type);
  if (cat === 'fire') return <FireSilhouette {...rest} />;
  if (cat === 'ems') return <EmsSilhouette {...rest} />;
  return <SpecialSilhouette {...rest} />;
}

/* ─────────────── Fire (rotes Löschfahrzeug) ─────────────── */
export function FireSilhouette(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 220 90"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Feuerwehrfahrzeug"
      {...props}
    >
      {/* Chassis shadow */}
      <ellipse cx="110" cy="82" rx="100" ry="4" fill="#000" opacity="0.25" />

      {/* Lower body */}
      <rect x="14" y="42" width="190" height="32" rx="4" fill="currentColor" />
      {/* Cab */}
      <path d="M14 42 L14 24 Q14 18 20 18 L66 18 L78 42 Z" fill="currentColor" />
      {/* Box body window-strip */}
      <rect x="86" y="46" width="100" height="6" fill="rgba(0,0,0,0.25)" />
      {/* Cab window */}
      <path d="M22 24 L62 24 L70 38 L22 38 Z" fill="rgba(255,255,255,0.85)" />
      {/* Roof lights */}
      <rect x="28" y="14" width="34" height="5" rx="2" fill="#3b82f6" />
      <rect x="64" y="14" width="34" height="5" rx="2" fill="#3b82f6" />
      {/* White stripe */}
      <rect x="14" y="56" width="190" height="4" fill="#ffffff" opacity="0.9" />
      {/* Door split */}
      <line x1="120" y1="46" x2="120" y2="74" stroke="rgba(0,0,0,0.3)" strokeWidth="1.2" />
      {/* Wheels */}
      <circle cx="48" cy="74" r="10" fill="#1f2937" />
      <circle cx="48" cy="74" r="4" fill="#94a3b8" />
      <circle cx="170" cy="74" r="10" fill="#1f2937" />
      <circle cx="170" cy="74" r="4" fill="#94a3b8" />
    </svg>
  );
}

/* ─────────────── EMS (orange RTW) ─────────────── */
export function EmsSilhouette(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 220 90"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Rettungsfahrzeug"
      {...props}
    >
      <ellipse cx="110" cy="82" rx="100" ry="4" fill="#000" opacity="0.25" />

      {/* Box body (taller) */}
      <rect x="60" y="22" width="148" height="52" rx="4" fill="currentColor" />
      {/* Cab */}
      <path d="M16 32 L16 50 L60 50 L60 22 L34 22 Q26 22 22 28 Z" fill="currentColor" />
      {/* Cab window */}
      <path d="M24 32 L52 32 L52 44 L24 44 Z" fill="rgba(255,255,255,0.85)" />
      {/* Patient area windows */}
      <rect x="78" y="34" width="22" height="12" fill="rgba(255,255,255,0.85)" />
      <rect x="106" y="34" width="22" height="12" fill="rgba(255,255,255,0.85)" />
      <rect x="134" y="34" width="22" height="12" fill="rgba(255,255,255,0.85)" />
      <rect x="180" y="34" width="22" height="12" fill="rgba(255,255,255,0.85)" />

      {/* White stripe */}
      <rect x="16" y="56" width="192" height="6" fill="#ffffff" opacity="0.9" />

      {/* Star-of-life */}
      <g transform="translate(140 66) rotate(0)" fill="#0ea5e9">
        <circle r="6" fill="#ffffff" />
        <rect x="-4" y="-1" width="8" height="2" />
        <rect x="-1" y="-4" width="2" height="8" />
        <rect x="-4" y="-1" width="8" height="2" transform="rotate(60)" />
        <rect x="-4" y="-1" width="8" height="2" transform="rotate(-60)" />
      </g>

      {/* Roof lights */}
      <rect x="80" y="18" width="80" height="5" rx="2" fill="#3b82f6" />

      {/* Wheels */}
      <circle cx="50" cy="74" r="10" fill="#1f2937" />
      <circle cx="50" cy="74" r="4" fill="#94a3b8" />
      <circle cx="180" cy="74" r="10" fill="#1f2937" />
      <circle cx="180" cy="74" r="4" fill="#94a3b8" />
    </svg>
  );
}

/* ─────────────── Special (Van/Transporter) ─────────────── */
export function SpecialSilhouette(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 220 90"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Spezialfahrzeug"
      {...props}
    >
      <ellipse cx="110" cy="82" rx="100" ry="4" fill="#000" opacity="0.25" />

      {/* Van body */}
      <path d="M16 36 Q16 28 24 28 L186 28 Q198 28 200 38 L200 74 L16 74 Z" fill="currentColor" />
      {/* Front window */}
      <path d="M22 38 L52 38 L52 52 L22 52 Z" fill="rgba(255,255,255,0.75)" />
      {/* Side windows (long strip) */}
      <rect x="62" y="38" width="124" height="14" fill="rgba(255,255,255,0.65)" />
      {/* Door split */}
      <line x1="120" y1="38" x2="120" y2="74" stroke="rgba(0,0,0,0.35)" strokeWidth="1.2" />
      {/* Pin stripe */}
      <rect x="16" y="58" width="184" height="3" fill="#ffffff" opacity="0.6" />

      {/* Roof rack */}
      <rect x="40" y="22" width="140" height="6" rx="2" fill="rgba(0,0,0,0.45)" />

      {/* Wheels */}
      <circle cx="50" cy="74" r="10" fill="#1f2937" />
      <circle cx="50" cy="74" r="4" fill="#94a3b8" />
      <circle cx="170" cy="74" r="10" fill="#1f2937" />
      <circle cx="170" cy="74" r="4" fill="#94a3b8" />
    </svg>
  );
}
